import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import { RECALL_GOALS } from '../../settings-recall-agent/types';
import type { RecallGoal } from '../../settings-recall-agent/types';
import {
  CLINICAL_ERROR,
  canContinueFromAudience,
  contactableNow,
  matchingNow,
  uncoveredFor,
} from '../../recall-builder/derive';
import { CONTACT_LABEL } from '../../recall-builder/types';
import type { AudiencePatient, RecallDraft, Suggestion } from '../../recall-builder/types';
import { HYGIENE_COUNT, HYGIENE_PATIENTS, HYGIENE_READBACK, SUGGESTIONS } from '../../recall-builder/mockData';
import { BuilderButton, BuilderFooter, BuilderHeader, BuilderPage } from './BuilderShell';

export interface AudienceViewProps {
  draft: RecallDraft;
  onChange: (next: RecallDraft) => void;
  onContinue: () => void;
  onCancel: () => void;
  /**
   * Editing an existing recall. **The goal becomes read-only** — changing it
   * would rewrite the suggestions, the available fields and the starting steps
   * underneath patients already part-way through, so the answer is to duplicate.
   */
  editing?: boolean;
}

const GOAL_BLURB: Record<RecallGoal, string> = {
  book_appointment: 'Bring patients in for care they are due for.',
  reactivate_patient: 'Win back patients who have not been in for a long time.',
  increase_loyalty: 'Birthday and thank-you messages. No booking request.',
  clinical_safety: 'Follow up on a clinical reason, which the description must name.',
};

const GOAL_WORD: Record<RecallGoal, string> = {
  book_appointment: 'booking',
  reactivate_patient: 'reactivation',
  increase_loyalty: 'loyalty',
  clinical_safety: 'clinical safety',
};

type BuildState = 'idle' | 'building' | 'done' | 'nothing' | 'failed';

/**
 * Page 1 — Audience (spec 21 §2).
 *
 * **There are no editable conditions in V1.** The description is the only input,
 * and ELVA reads back exactly what it understood. With no conditions on screen the
 * readback is the only way to see what will run, so it has to be exactly true —
 * which is why **its lines come from the API**, written by code from the same
 * criteria that run, rather than composed here.
 */
export function AudienceView({ draft, onChange, onContinue, onCancel, editing = false }: AudienceViewProps) {
  const [state, setState] = useState<BuildState>(draft.readback === undefined ? 'idle' : 'done');
  const [description, setDescription] = useState(draft.description);
  const [pickedSuggestion, setPickedSuggestion] = useState<string | null>(null);
  const [seeAll, setSeeAll] = useState(false);
  const [showLeftOut, setShowLeftOut] = useState(false);

  const goal = draft.goal;
  const suggestions: Suggestion[] =
    goal === undefined ? [] : [...SUGGESTIONS[goal]].sort((a, b) => uncoveredFor(b) - uncoveredFor(a));

  const build = (text: string) => {
    setState('building');
    // The description never runs a query: it returns criteria from a closed
    // vocabulary, and the readback is written from those same criteria.
    window.setTimeout(() => {
      if (text.trim().length < 4) {
        setState('nothing');
        onChange({ ...draft, description: text, readback: undefined as never, patients: [], leftOut: [] });
        return;
      }
      setState('done');
      onChange({
        ...draft,
        description: text,
        readback: HYGIENE_READBACK,
        count: HYGIENE_COUNT,
        patients: HYGIENE_PATIENTS,
        leftOut: [],
      });
    }, 350);
  };

  const setGoal = (next: RecallGoal) => {
    onChange({ ...draft, goal: next });
    setPickedSuggestion(null);
  };

  const leaveOut = (id: string) => {
    onChange({ ...draft, leftOut: [...draft.leftOut, id] });
  };
  const putBack = (id: string) => {
    onChange({ ...draft, leftOut: draft.leftOut.filter((x) => x !== id) });
  };

  const visible = draft.patients.filter((p) => !draft.leftOut.includes(p.id));
  // Counted, not written. It said *The first 20* above six rows (§5.3).
  const shownSub = `The first ${String(visible.length)}, and why each one is here.`;
  // Recalculated after every build and every Leave out — never a stale number,
  // and from the same helper pages 2 and 3 use.
  const match = matchingNow(draft);
  const contactable = contactableNow(draft);
  const clinicalMissing =
    goal === 'clinical_safety' && draft.readback !== undefined && !draft.readback.hasClinicalCriterion;

  return (
    <BuilderPage>
      <BuilderHeader
        active="audience"
        titleLead="Who is this"
        titleAccent="for"
        titleTail="?"
        sub="Choose what the recall is for, then describe who should get it."
      />

      <div className="mt-6 flex flex-col gap-[22px]">
        {/* ① The goal — first, because everything after it depends on it. */}
        <Card
          title="What is this recall for?"
          sub={
            editing
              ? 'To change the goal, duplicate this recall — changing it here would rewrite the messages under patients who are part-way through.'
              : 'This decides the suggestions below, the fields you can use in messages, and the steps you start with.'
          }
        >
          <div className="grid grid-cols-4 gap-3 px-6 pb-[19px] max-[1100px]:grid-cols-2">
            {RECALL_GOALS.map((g) => {
              const on = goal === g.id;
              if (editing && !on) return null;
              return (
                <label
                  key={g.id}
                  className={cn(
                    'rounded-[12px] border p-3.5',
                    editing ? 'cursor-default' : 'cursor-pointer',
                    on
                      ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
                      : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]',
                  )}
                >
                  <input
                    type="radio"
                    name="rb-goal"
                    checked={on}
                    disabled={editing}
                    onChange={() => { setGoal(g.id); }}
                    className="sr-only"
                  />
                  <span className="block text-[13.5px] font-bold text-[var(--text-primary)]">
                    {g.id === 'book_appointment'
                      ? 'Book an appointment'
                      : g.id === 'reactivate_patient'
                        ? 'Reactivate a patient'
                        : g.id === 'increase_loyalty'
                          ? 'Increase loyalty'
                          : 'Clinical safety'}
                  </span>
                  <span className="mt-1 block text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
                    {GOAL_BLURB[g.id]}
                  </span>
                </label>
              );
            })}
          </div>
        </Card>

        {/* ② Suggestions and the description. */}
        {goal !== undefined ? (
          <Card title="Who should get it?" sub="Pick a suggestion or describe the patients in your own words.">
            <div className="px-6 pb-[19px]">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
                Suggestions for {GOAL_WORD[goal]} · sorted by patients no recall reaches yet
              </div>
              <div className="mt-2.5 grid grid-cols-3 gap-2.5 max-[1100px]:grid-cols-2">
                {suggestions.map((s) => (
                  <SuggestionTile
                    key={s.id}
                    suggestion={s}
                    selected={pickedSuggestion === s.id}
                    onPick={() => {
                      setPickedSuggestion(s.id);
                      setDescription(s.description);
                      build(s.description);
                    }}
                  />
                ))}
              </div>

              <label
                htmlFor="rb-description"
                className="mt-5 block text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]"
              >
                Describe the patients
              </label>
              <div className="mt-2 flex items-start gap-3">
                <textarea
                  id="rb-description"
                  rows={3}
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); }}
                  placeholder="For example: patients due for hygiene who have been in within the past two years."
                  className="min-h-[76px] flex-1 resize-y rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)] px-3.5 py-3 text-[14px] leading-[1.6] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
                />
                <BuilderButton kind="primary" onClick={() => { build(description); }}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon kind="search" size={13} stroke={2.2} />
                    {state === 'building' ? 'Building…' : 'Build the list'}
                  </span>
                </BuilderButton>
              </div>
              <div className="mt-1.5 text-[11.5px] text-[var(--text-muted)]">
                To change who gets this recall, change the description and build again. Your messages are kept.
              </div>

              {state === 'nothing' ? (
                <div className="mt-4 rounded-[12px] bg-[#FBF3E4] px-4 py-3 text-[12.5px] leading-[1.65] text-[var(--warn-amber)]">
                  ELVA couldn’t find anyone from that description. Try one of the suggestions, or say who they are in a
                  different way.
                </div>
              ) : null}

              {draft.readback !== undefined && state !== 'nothing' ? (
                <ReadbackBox readback={draft.readback} clinicalError={clinicalMissing} />
              ) : null}
            </div>
          </Card>
        ) : null}

        {/* ③ The count, and every missing person accounted for. While a rebuild is
            running this DIMS rather than clearing (§2.3) — blanking the numbers
            makes it look as though the audience just became empty. */}
        {draft.count !== undefined && state !== 'nothing' ? (
          <div
            className={cn(
              'rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] px-6 py-5',
              state === 'building' && 'opacity-55',
            )}
          >
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-[var(--font-mono)] text-[26px] font-bold tracking-[-0.03em] text-[var(--text-primary)]">
                {match.toLocaleString('en-US')}
              </span>
              <span className="text-[13px] text-[var(--text-secondary)]">match →</span>
              <span className="font-[var(--font-mono)] text-[26px] font-bold tracking-[-0.03em] text-[var(--money-green)]">
                {contactable.toLocaleString('en-US')}
              </span>
              <span className="text-[13px] text-[var(--text-secondary)]">can be contacted now</span>
            </div>
            <p className="m-0 mt-2 text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
              The other {match - contactable}:{' '}
              {draft.count.reasons.map((r, i) => (
                <React.Fragment key={r.label}>
                  {i > 0 ? ' · ' : ''}
                  <b className="font-semibold text-[var(--text-primary)]">{r.n}</b> {r.label}
                </React.Fragment>
              ))}
              .
            </p>
            {/* The first two stay in and are tried again later — say so, or the
                numbers look like people who were dropped. */}
            <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">
              The {draft.count.reasons.filter((r) => r.staysIn).map((r) => r.n).join(' and the ')} stay in and are tried
              again later.
            </p>
            {draft.leftOut.length > 0 ? (
              <div className="mt-2 text-[11.5px] text-[var(--text-muted)]">
                {draft.leftOut.length} left out by hand ·{' '}
                <button
                  type="button"
                  onClick={() => { setShowLeftOut(!showLeftOut); }}
                  className="font-semibold text-[var(--brand-indigo)] hover:underline"
                >
                  {showLeftOut ? 'Hide' : 'Show'}
                </button>
                {showLeftOut ? (
                  <span className="mt-1 block">
                    {draft.leftOut.map((id) => {
                      const p = draft.patients.find((x) => x.id === id);
                      return (
                        <span key={id} className="mr-3 inline-flex items-center gap-1.5">
                          {p?.name ?? id}
                          <button
                            type="button"
                            onClick={() => { putBack(id); }}
                            className="font-semibold text-[var(--brand-indigo)] hover:underline"
                          >
                            Put back
                          </button>
                        </span>
                      );
                    })}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ④ The patients. Same rule: dimmed while rebuilding, not emptied. */}
        {draft.count !== undefined && state !== 'nothing' ? (
          <div className={cn(state === 'building' && 'opacity-55')}>
          <Card
            title="The patients"
            sub={shownSub}
            headRight={
              <button
                type="button"
                onClick={() => { setSeeAll(true); }}
                className="text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline"
              >
                See all {match.toLocaleString('en-US')} →
              </button>
            }
          >
            {visible.length === 0 ? (
              <p className="m-0 px-6 pb-[19px] text-[12.5px] text-[var(--text-muted)]">
                No patients match this description right now.
              </p>
            ) : (
              <PatientTable patients={visible} onLeaveOut={leaveOut} />
            )}
          </Card>
          </div>
        ) : null}
      </div>

      <BuilderFooter note="Saved as a draft as you go. You can leave and come back.">
        <BuilderButton onClick={onCancel}>Cancel</BuilderButton>
        <BuilderButton
          kind="primary"
          disabled={!canContinueFromAudience(draft)}
          title={clinicalMissing ? CLINICAL_ERROR : undefined}
          onClick={onContinue}
        >
          Continue to messages
        </BuilderButton>
      </BuilderFooter>

      <ViewportDrawer open={seeAll} onClose={() => { setSeeAll(false); }} width="min(880px, 94vw)">
        <AllPatientsDrawer patients={visible} total={match} onLeaveOut={leaveOut} onClose={() => { setSeeAll(false); }} />
      </ViewportDrawer>
    </BuilderPage>
  );
}

function Card({
  title,
  sub,
  headRight,
  children,
}: {
  title: string;
  sub: React.ReactNode;
  headRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)]">
      <header className="flex items-start justify-between gap-4 px-6 pb-3 pt-[19px]">
        <div className="min-w-0">
          <h2 className="m-0 text-[15px] font-bold tracking-[-0.01em] text-[var(--text-primary)]">{title}</h2>
          <p className="m-0 mt-1 max-w-[640px] text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>
        </div>
        {headRight ? <div className="shrink-0">{headRight}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** Two live counts, and an unavailable one says why rather than vanishing. */
function SuggestionTile({
  suggestion,
  selected,
  onPick,
}: {
  suggestion: Suggestion;
  selected: boolean;
  onPick: () => void;
}) {
  const unavailable = suggestion.unavailable !== undefined;
  const uncovered = uncoveredFor(suggestion);
  const healthy = uncovered >= suggestion.matches / 2;
  return (
    <button
      type="button"
      disabled={unavailable}
      onClick={onPick}
      className={cn(
        'rounded-[12px] border p-3 text-left',
        unavailable
          ? 'cursor-not-allowed border-dashed border-[var(--border-default)] bg-[var(--surface-sunken)] opacity-70'
          : selected
            ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
            : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]',
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{suggestion.title}</span>
        {selected && !unavailable ? (
          <Icon kind="check" size={13} stroke={3} className="shrink-0 text-[var(--brand-indigo)]" />
        ) : null}
      </span>
      {unavailable ? (
        <span className="mt-1 block text-[11px] leading-[1.5] text-[var(--text-muted)]">{suggestion.unavailable}</span>
      ) : (
        <span className="mt-1.5 block text-[11px] text-[var(--text-muted)]">
          <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{suggestion.matches}</b> patients ·{' '}
          <b
            className="font-[var(--font-mono)] font-bold"
            style={{ color: healthy ? 'var(--money-green)' : 'var(--warn-amber)' }}
          >
            {uncovered}
          </b>{' '}
          not in a recall yet
        </span>
      )}
    </button>
  );
}

/** What ELVA understood, what it left out and why, and the standing exclusions. */
function ReadbackBox({
  readback,
  clinicalError,
}: {
  readback: RecallDraft['readback'];
  clinicalError: boolean;
}) {
  if (readback === undefined) return null;
  return (
    <div className="mt-4 rounded-[12px] bg-[#EEF6F2] px-4 py-3.5">
      <div className="text-[12.5px] font-bold text-[#1B5F45]">ELVA understood this as</div>
      <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
        {readback.understood.map((line) => (
          <li key={line} className="flex items-start gap-2 text-[12.5px] leading-[1.6] text-[var(--text-secondary)]">
            <Icon kind="check" size={12} stroke={3} className="mt-[3px] shrink-0 text-[var(--money-green)]" />
            {line}
          </li>
        ))}
      </ul>

      {readback.leftOut.map((l) => (
        <div
          key={l.phrase}
          className="mt-2.5 flex items-start gap-2 rounded-[9px] bg-[#FBF3E4] px-3 py-2 text-[12px] leading-[1.6] text-[var(--warn-amber)]"
        >
          <Icon kind="alert" size={12} stroke={2.4} className="mt-[3px] shrink-0" />
          <span>
            <b className="font-semibold">Left out: “{l.phrase}”.</b> {l.reason}
          </span>
        </div>
      ))}

      <p className="m-0 mt-2.5 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
        <b className="font-semibold text-[var(--text-secondary)]">Always left out:</b> {readback.alwaysLeftOut}
      </p>

      {clinicalError ? (
        <p className="m-0 mt-2.5 rounded-[9px] bg-[#FBEDEC] px-3 py-2 text-[12px] leading-[1.6] text-[var(--alert-red)]">
          {CLINICAL_ERROR}
        </p>
      ) : null}
    </div>
  );
}

const CONTACT_TONE: Record<AudiencePatient['contact'], string> = {
  will: 'var(--money-green)',
  later: 'var(--warn-amber)',
  opted_out: 'var(--text-muted)',
};

function PatientTable({
  patients,
  onLeaveOut,
}: {
  patients: AudiencePatient[];
  onLeaveOut: (id: string) => void;
}) {
  return (
    <div>
      <div
        className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.6fr)_90px_70px_170px_84px] gap-3 bg-[#FAFBFE] px-6 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]"
        style={{ borderTop: '1px dotted #DFE3ED' }}
      >
        <span>Patient</span>
        <span>Why they are here</span>
        <span>Last visit</span>
        <span>Due</span>
        <span>Contact</span>
        <span />
      </div>
      {patients.map((p, i) => (
        <div
          key={p.id}
          className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.6fr)_90px_70px_170px_84px] items-center gap-3 px-6 py-[11px]"
          style={i === patients.length - 1 ? undefined : { borderBottom: '1px dotted #DFE3ED' }}
        >
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-[var(--text-primary)]">{p.name}</span>
            <span className="block text-[11px] text-[var(--text-muted)]">{p.meta}</span>
          </span>
          <span className="min-w-0 truncate text-[12px] text-[var(--text-secondary)]">{p.why}</span>
          <span className="text-[12px] text-[var(--text-secondary)]">{p.lastVisit}</span>
          <span className="text-[12px] text-[var(--text-secondary)]">{p.due}</span>
          <span className="inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: CONTACT_TONE[p.contact] }}>
            <span className="h-[6px] w-[6px] rounded-full" style={{ background: CONTACT_TONE[p.contact] }} />
            {CONTACT_LABEL[p.contact]}
          </span>
          <button
            type="button"
            onClick={() => { onLeaveOut(p.id); }}
            className="justify-self-end text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline"
          >
            Leave out
          </button>
        </div>
      ))}
    </div>
  );
}

/** See all — paginated in principle, searchable, same columns. */
function AllPatientsDrawer({
  patients,
  total,
  onLeaveOut,
  onClose,
}: {
  patients: AudiencePatient[];
  total: number;
  onLeaveOut: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const shown = patients.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-[var(--border-default)] px-6 py-4">
        <span>
          <h2 className="m-0 text-[16px] font-bold text-[var(--text-primary)]">Everyone in this recall</h2>
          {/* Says how many of the total are actually listed, rather than claiming
              all of them and showing a handful (§5.3). */}
          <span className="mt-0.5 block text-[11.5px] text-[var(--text-muted)]">
            {patients.length} of {total.toLocaleString('en-US')} listed here
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)]"
        >
          <Icon kind="x" size={14} stroke={2} />
        </button>
      </header>
      <div className="border-b border-[var(--border-default)] px-6 py-3">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); }}
          placeholder="Search by name"
          aria-label="Search patients by name"
          className="w-full rounded-[10px] border border-[#dfe4ee] px-3 py-2 text-[13px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="m-0 px-6 py-8 text-center text-[12.5px] text-[var(--text-muted)]">Nobody matches that name.</p>
        ) : (
          <PatientTable patients={shown} onLeaveOut={onLeaveOut} />
        )}
      </div>
    </div>
  );
}
